<template>
  <div
    :class="[
      $style.base,
      {
        [$style.isTouch]: touch,
        [$style.isMobile]: mobile,
        [$style.showArrows]: showArrows
      }
    ]"
    v-test="'week-view'"
  >
    <div :class="$style.container" ref="container">
      <div :class="$style.list" ref="list">
        <DatePickerItem
          v-for="(crtDate, index) in dates"
          :key="index"
          :data="getEmptyAvailability(crtDate)"
          :isLoading="loadingAvailability && !hasAvailability(crtDate)"
          @select="onDateSelect"
          @scrollToView="scrollOnDateSelect"
          v-test="'date-item'"
        />
      </div>
    </div>
    <div
      v-if="showArrows"
      :class="[
        $style.arrow,
        $style.arrowLeft,
        { [$style.isDisabled]: scrollLeft === 0 }
      ]"
      @click="slideLeft"
    >
      <IconArrow
        :isDisabled="scrollLeft === 0"
        direction="left"
        v-test="'week-view-left-arrow'"
      />
    </div>
    <div
      v-if="showArrows"
      :class="[
        $style.arrow,
        $style.arrowRight,
        { [$style.isDisabled]: endOfScroll }
      ]"
      @click="slideRight"
    >
      <IconArrow
        :isDisabled="isAllAvailabilityDataLoaded && endOfScroll"
        direction="right"
        v-test="'week-view-right-arrow'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Availability } from '@/types';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { DEFAULT_RANGE, useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { device } from '@/user-context';
import { useScroll } from './use-scroll';

import IconArrow from '@/components/IconArrow.vue';
import { useCompanyStore } from '@/stores/company';
import dayjs from 'dayjs';
import DatePickerItem from './DatePickerItem.vue';

const emits = defineEmits(['loadAvailabilityData']);

const { date } = storeToRefs(useBookingStore());

const { settings } = useCompanyStore();

const dates = ref<string[]>([dayjs().format('YYYY-MM-DD')]);
const dateMarker = computed(() => dayjs(dates.value[dates.value.length - 1]));

const addEmptyDates = (amount: number) => {
  const lastDate = dateMarker.value;
  const lastBookableDate = dayjs(settings.last_bookable_day);

  const daysTillLastBookableDate = lastBookableDate.diff(lastDate, 'day');

  const daysToAdd = Math.min(daysTillLastBookableDate, amount);

  for (let i = 1; i <= daysToAdd; i++) {
    dates.value.push(lastDate.add(i, 'day').format('YYYY-MM-DD'));
  }
};

addEmptyDates(
  date.value
    ? dayjs(date.value).diff(dayjs(), 'day') + DEFAULT_RANGE
    : DEFAULT_RANGE
);

const {
  availabilities,
  loadingAvailability,
  isAllDataLoaded: isAllAvailabilityDataLoaded
} = storeToRefs(useAvailabilityStore());
const { getEmptyAvailability, hasAvailability } = useAvailabilityStore();

const container = ref<HTMLElement | null>(null);
const list = ref<HTMLElement | null>(null);

const { touch, mobile } = device;

const showArrows = computed(
  () => !touch && dates.value && dates.value.length > 7
);

const {
  scrollLeft,
  endOfScroll,
  slideLeft,
  slideRight,
  scrollToView,
  onEndOfScroll
} = useScroll(container);

const onDateSelect = (offsetLeft: number, availabilityData: Availability) => {
  date.value = availabilityData.date;
  scrollOnDateSelect(offsetLeft);
};

const scrollOnDateSelect = (offsetLeft: number) => {
  scrollToView(offsetLeft);
};

onEndOfScroll((scrollLeftRatio: number) => {
  if (scrollLeftRatio > dates.value.length / DEFAULT_RANGE - 1) {
    addEmptyDates(DEFAULT_RANGE);
  }

  if (
    !loadingAvailability.value &&
    scrollLeftRatio > availabilities.value.length / DEFAULT_RANGE - 1
  ) {
    emits('loadAvailabilityData');
  }
});

watch(
  availabilities,
  () => {
    addEmptyDates(DEFAULT_RANGE);
  },
  { immediate: true }
);

watch(
  date,
  () => {
    if (availabilities.value.length > dates.value.length) {
      addEmptyDates(
        dayjs(date.value).diff(dateMarker.value, 'day') + DEFAULT_RANGE
      );
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" module>
.base {
  position: relative;
  width: 100%;
  margin-top: $spacing;
  overflow: auto;

  &.showArrows {
    padding-left: $slider-btn-size;
    padding-right: $slider-btn-size;
  }

  &:not(.showArrows):not(.isTouch) {
    padding-left: $spacing - 3px;
  }

  &.isTouch {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 0;
      width: $spacing;
      height: 100%;
      z-index: 1;
      pointer-events: none;
    }

    &:before {
      left: 0;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
    &:after {
      right: 0;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }
}

.arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  height: 100%;
  width: $slider-btn-size;
  cursor: pointer;

  &.isDisabled {
    pointer-events: none;
  }
}

.arrowLeft {
  left: 0;
  background: linear-gradient(to right, $color-bg, transparent);
}

.arrowRight {
  right: 0;
  background: linear-gradient(to right, transparent, $color-bg);
}

.container {
  padding-top: $spacing;
  padding-bottom: $spacing;

  .base:not(.isTouch) & {
    overflow: auto;

    //Hide scrollbars since overflow is auto
    -ms-overflow-style: none; // IE and Edge
    scrollbar-width: none; // Firefox

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .base.isMobile & {
    width: 100vw; // Fixes a mobile Safari bug with overflow-x: scroll, which seems to break when the container has no width, or a percentage based width
  }

  .base.isTouch & {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }
}
.list {
  display: flex;

  .base.isTouch & {
    padding-left: $spacing;
  }
}
</style>
